import styled from '@emotion/styled';
import { Icon, Link, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';

export interface Step<TId extends string = string> {
  text: string;
  id: TId;
  previouslyCompleted?: boolean;
}

export type State = 'active' | 'done' | 'disabled';

type Props<TStepId extends string> = {
  step: Step<TStepId> & { state: State };
  onStepClick: (step: Step<TStepId>) => void;
};

const S = {
  Icon: styled(Icon)`
    margin-right: ${tokens.spacer.x1};
  `,
  ScreenReaderOnly: styled.span`
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  `,
};

export function HeaderStep<TStepId extends string>({
  step,
  onStepClick,
}: Props<TStepId>): JSX.Element {
  const { t } = useTranslationWithPrefix('checkout:header');

  return (
    <>
      {(step.state === 'done' || step.previouslyCompleted === true) && (
        // Added the className for testing, as adding data-testid will be ignored.
        <S.Icon
          size="m"
          icon="check_alt"
          color="text_success"
          className={`${step.id}-done-icon`}
          description={t('completed')}
        />
      )}

      {step.state === 'disabled' && <S.ScreenReaderOnly>{t('not-completed')}</S.ScreenReaderOnly>}

      <Link
        as="button"
        kind="inline"
        isDisabled={step.state === 'disabled'}
        style={{
          textDecoration: step.state === 'done' ? 'underline' : 'none',
        }}
        onClick={() => onStepClick(step)}
      >
        {step.text}
      </Link>
    </>
  );
}
